import { Flex, Heading, useDisclosure } from "@chakra-ui/react";
import { Button } from "@chakra-ui/react";
import { useContext, useEffect, useState } from "react";
import { apiGetRequest, apiRequest } from "utils/api";
import { LoanContext } from "providers/loan";
import { AlertsContext } from "providers/alerts";
import { FeeTable } from "./fee-table";
import { FeeModal } from "./fee-modal";
import { FeeModel } from "providers/loans/fee.model";
import { LoanModel } from "providers/loans/loan.model";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { useXeroAccountsQuery } from "modules/xero/queries/use-xero-accounts-query";

interface FeeTabProps {
  title?: string;
  loan: LoanModel;
}

export function FeeTab({ loan, title }: FeeTabProps) {
  const { user } = useTenant();
  const { showSuccess, showError } = useContext(AlertsContext);
  const { fees, setFees } = useContext(LoanContext);

  const [loading, setLoading] = useState(false);
  const [fee, setFee] = useState<FeeModel | null>(null);

  const { data: xeroFeeAccounts = [] } = useXeroAccountsQuery(loan.id, "fee");

  const { isOpen, onOpen, onClose } = useDisclosure();

  async function getFeePayments() {
    try {
      const { data } = await apiRequest("/supabase/fees/select", {
        loan: loan!.id,
      });
      setFees(data);
    } catch (error) {
      console.error(error);
    }
  }

  function closeFeeModal() {
    setFee(null);
    onClose();
  }

  function handleAddFee() {
    const funding_date = loan?.funding_date ?? null;
    if (!funding_date) {
      showError({
        title: "Funding date not set",
        description: "Please set the funding date",
      });
    } else {
      setFee({
        id: 0,
        description: "",
        fee_amount: null,
        fee_date: "",
        account: null,
        created_at: "",
        loan: loan!.id,
      });
      onOpen();
    }
  }

  async function handleDelete(id: any) {
    try {
      setLoading(true);
      await apiRequest("/supabase/fees/delete", {
        id: id,
      });
      showSuccess({
        title: "fee Deleted",
        description: "The fee has been deleted",
      });
    } catch (error) {
      showError({
        title: "Something went wrong",
        description: "error",
      });
    } finally {
      setLoading(false);
      getFeePayments();
    }
  }

  async function saveEditFees(updatedFee: FeeModel) {
    if (!updatedFee.id) {
      await getFeePayments();
    } else {
      const updatedFees = fees.map((fee: FeeModel) => {
        if (fee.id === updatedFee.id) {
          return updatedFee;
        }

        return fee;
      });

      setFees(updatedFees);
    }
  }

  function handleEdit(fee: FeeModel) {
    setFee(fee);
    onOpen();
  }

  return (
    <>
      {fee && (
        <FeeModal
          isOpen={isOpen}
          onClose={closeFeeModal}
          saveFee={saveEditFees}
          fee={fee}
          loan={loan!}
          accounts={xeroFeeAccounts}
        />
      )}
      <Flex direction="column" gap={4}>
        <Flex justifyContent={title ? "space-between" : "flex-end"} alignItems="center">
          {title && (
            <Heading fontSize="lg" fontWeight="bold">
              {title}
            </Heading>
          )}
          {user.type === "lender" && (
            <Button onClick={() => handleAddFee()} isLoading={loading} variant="white" size="sm">
              Add Fee
            </Button>
          )}
        </Flex>

        <FeeTable
          fees={fees}
          handleEdit={handleEdit}
          handleDelete={handleDelete}
          accounts={xeroFeeAccounts}
        />
      </Flex>
    </>
  );
}
